import React, { useMemo, useState } from 'react';
import { Plus } from 'lucide-react';
import {
  AppealType,
  APPEAL_TYPE_LABELS,
  DCM_FUNNEL_VISUAL,
  DcmFunnelStageId,
  DcmSegmentMaster,
  DcmWho,
  FUNNEL_STAGE_OPTIONS,
} from '../../../types/knowledge';

interface StrategyMatrixProps {
  segments: DcmSegmentMaster[];
  who: DcmWho[];
  selectedWhoId?: string | null;
  onSelectWho: (who: DcmWho) => void;
  onAddPersona: (segmentId: string, funnelStageId: DcmFunnelStageId) => void;
  onAddSegment?: () => void;
  onEditSegment?: (segment: DcmSegmentMaster) => void;
}

// ペルソナアバターのグラデーション（モックの p.grad 相当。who_id で安定割り当て）
const AVATAR_GRADIENTS = [
  'linear-gradient(135deg, #616FC9, #9675C9)',
  'linear-gradient(135deg, #C9619C, #E0894B)',
  'linear-gradient(135deg, #4F86C6, #44C2A4)',
  'linear-gradient(135deg, #9675C9, #C9619C)',
];

const avatarGradient = (whoId: string): string => {
  let hash = 0;
  for (let i = 0; i < whoId.length; i += 1) {
    hash = (hash * 31 + whoId.charCodeAt(i)) & 0xffff;
  }
  return AVATAR_GRADIENTS[hash % AVATAR_GRADIENTS.length];
};

const APPEAL_TYPE_COLORS: Record<AppealType, string> = {
  functional: '#616FC9',
  value: '#44C2A4',
  trust: '#9675C9',
  feature: '#616FC9',
  merit: '#4F86C6',
  benefit: '#44C2A4',
  emotion: '#C9619C',
  negative: '#C9A061',
  offer: '#E0894B',
  comparison: '#5BA8C4',
  social: '#6FA86F',
};

const StrategyMatrix: React.FC<StrategyMatrixProps> = ({
  segments,
  who,
  selectedWhoId,
  onSelectWho,
  onAddPersona,
  onAddSegment,
  onEditSegment,
}) => {
  const [isScrolled, setIsScrolled] = useState(false);
  const whoByCell = useMemo(() => {
    const map = new Map<string, DcmWho[]>();
    who.forEach((item) => {
      const key = `${item.segment_id}:${item.funnel_stage_id}`;
      map.set(key, [...(map.get(key) || []), item]);
    });
    return map;
  }, [who]);

  return (
    <div className="overflow-hidden rounded-xl bg-[#f8f7f3]">
      <div
        className="matrix-scroll group overflow-x-auto pb-1"
        data-scrolled={isScrolled}
        onScroll={(event) => setIsScrolled(event.currentTarget.scrollLeft > 2)}
      >
        <div
          className="grid min-w-[920px] grid-cols-[142px_repeat(var(--cols,3),minmax(216px,1fr))_52px] gap-2"
          style={{
            ['--cols' as string]: Math.max(segments.length, 1),
          }}
        >
          <div className="sticky left-0 z-[6] flex items-center gap-[9px] rounded-[10px] py-2 pl-3 pr-2 shadow-[8px_0_0_0_#f8f7f3] transition-[box-shadow] duration-150 group-data-[scrolled=true]:shadow-[8px_0_0_0_#f8f7f3,12px_0_13px_-8px_rgba(31,23,18,0.18)] bg-[#f8f7f3]">
            <span className="text-[11px] font-semibold leading-snug text-gray-400">ファネル ＼ セグメント</span>
          </div>
          {segments.map((segment) => (
            <button
              key={segment.segment_id}
              type="button"
              className="group/segment relative cursor-pointer rounded-[10px] border border-[#dcd6c9] bg-white px-3 py-2.5 text-center transition-colors duration-[120ms] hover:border-primary hover:bg-primary/[0.03]"
              onClick={() => onEditSegment?.(segment)}
              aria-label={`セグメントを編集: ${segment.segment_name}`}
            >
              <span className="material-icons-round absolute right-1.5 top-1.5 text-[13px] text-primary opacity-0 transition-opacity duration-[120ms] group-hover/segment:opacity-100">
                edit
              </span>
              <span className="block text-[13px] font-bold leading-snug text-dark">{segment.segment_name}</span>
              {segment.definition && (
                <span className="mt-1 line-clamp-2 block text-[11px] leading-5 text-gray-500">{segment.definition}</span>
              )}
            </button>
          ))}
          <button
            type="button"
            className="flex cursor-pointer items-center justify-center rounded-[10px] border-[1.5px] border-dashed border-[#d9d6cd] bg-transparent text-[#b0ac9f] transition-all duration-[120ms] hover:border-primary hover:bg-primary/[0.05] hover:text-primary"
            onClick={onAddSegment}
            aria-label="セグメントを追加"
          >
            <Plus className="h-4 w-4" />
          </button>

          {FUNNEL_STAGE_OPTIONS.map((stage) => {
            const v = DCM_FUNNEL_VISUAL[stage.value];
            return (
            <React.Fragment key={stage.value}>
              <div className="sticky left-0 z-[6] flex items-center gap-[9px] rounded-[10px] py-2 pl-3 pr-2 shadow-[8px_0_0_0_#f8f7f3] transition-[box-shadow] duration-150 group-data-[scrolled=true]:shadow-[8px_0_0_0_#f8f7f3,12px_0_13px_-8px_rgba(31,23,18,0.18)]" style={{ background: v.labelBg }}>
                <span className="material-icons-round text-[18px]" style={{ color: v.iconColor }}>
                  {v.icon}
                </span>
                <div>
                  <div className="text-[12px] font-bold leading-tight" style={{ color: v.labelColor }}>{stage.label}</div>
                  <div className="text-[10px]" style={{ color: v.subColor }}>{stage.sub}</div>
                </div>
              </div>
              {segments.map((segment) => {
                const cellWho = whoByCell.get(`${segment.segment_id}:${stage.value}`) || [];
                // 空セルはモック準拠で「破線ボックス自体がセル」（白カードで包まない）
                if (cellWho.length === 0) {
                  return (
                    <button
                      key={`${segment.segment_id}-${stage.value}`}
                      type="button"
                      className="flex min-h-[80px] w-full cursor-pointer items-center justify-center gap-[5px] rounded-xl border-[1.5px] border-dashed border-[#d9d6cd] bg-transparent text-xs font-semibold text-[#b0ac9f] transition-all duration-[140ms] hover:border-solid hover:border-primary hover:bg-primary/[0.05] hover:text-primary"
                      onClick={() => onAddPersona(segment.segment_id, stage.value)}
                    >
                      <Plus className="h-4 w-4" />
                      <span>ペルソナを追加</span>
                    </button>
                  );
                }
                const hasSelected = selectedWhoId != null && cellWho.some((item) => item.who_id === selectedWhoId);
                return (
                  <div
                    key={`${segment.segment_id}-${stage.value}`}
                    className={`flex min-h-[80px] flex-col gap-[5px] rounded-xl border bg-white p-[7px] ${
                      hasSelected
                        ? 'border-primary shadow-[inset_0_0_0_1px_#616fc9,0_6px_18px_rgba(97,111,201,0.18)]'
                        : 'border-[#dcd6c9] shadow-[0_1px_2px_rgba(20,18,28,0.04)]'
                    }`}
                  >
                    {cellWho.map((item, whoIndex) => (
                      <div
                        key={item.who_id}
                        className={`rounded-[9px] border-[1.5px] px-0.5 py-[3px] ${whoIndex > 0 ? 'mt-[5px]' : ''} ${
                          item.who_id === selectedWhoId
                            ? 'border-[#c7ccf0] bg-[#eef0fa]'
                            : 'border-transparent bg-transparent'
                        }`}
                      >
                        <button
                          type="button"
                          className="flex w-full cursor-pointer items-center gap-1.5 rounded-md border-none bg-transparent px-[3px] py-0.5 transition-[background] duration-[120ms] hover:bg-primary/[0.09]"
                          onPointerDown={(event) => {
                            event.preventDefault();
                            onSelectWho(item);
                          }}
                        >
                          <span
                            className="inline-flex h-5 w-5 flex-shrink-0 items-center justify-center rounded-full text-[10px] font-bold text-white"
                            style={{ background: avatarGradient(item.who_id) }}
                          >
                            <span className="material-icons-round text-[13px] text-white">person</span>
                          </span>
                          <span className="min-w-0 flex-1 overflow-hidden text-ellipsis whitespace-nowrap text-left text-[10.5px] font-bold leading-[1.2] text-gray-600">
                            {item.who_name}
                          </span>
                        </button>
                        {item.appeals.slice(0, 4).map((appeal) => (
                          <button
                            key={appeal.appeal_id}
                            type="button"
                            className="mt-1 flex w-full cursor-pointer items-center gap-1.5 rounded-[7px] border border-warm-border bg-white px-[7px] py-[5px] transition-[border-color,box-shadow,transform] duration-[120ms] hover:-translate-y-px hover:border-[#aeb5de] hover:shadow-[0_3px_10px_rgba(97,111,201,0.13)]"
                            title={`訴求タイプ: ${APPEAL_TYPE_LABELS[appeal.type] || appeal.type} ／ 訴求軸: ${appeal.appeal_axis || '-'}`}
                            onPointerDown={(event) => {
                              event.preventDefault();
                              onSelectWho(item);
                            }}
                          >
                            <span
                              className="h-[14px] w-[3px] flex-shrink-0 rounded-sm"
                              style={{ backgroundColor: APPEAL_TYPE_COLORS[appeal.type] || '#616FC9' }}
                            />
                            <span className="flex min-w-0 flex-1 flex-col gap-px text-left">
                              {/* ⑨: タイプ名（例:「機能・特長訴求」）ではなく訴求軸を表示する。長い場合は truncate。 */}
                              <span className="overflow-hidden text-ellipsis whitespace-nowrap text-[9px] font-bold leading-[1.15] text-[#a87c2b]">
                                {appeal.appeal_axis || '訴求軸未設定'}
                              </span>
                              <span className="overflow-hidden text-ellipsis whitespace-nowrap text-[11px] font-semibold leading-[1.2] text-dark">
                                {appeal.name}
                              </span>
                            </span>
                          </button>
                        ))}
                      </div>
                    ))}
                    <button
                      type="button"
                      className="flex w-full cursor-pointer items-center justify-center gap-[3px] rounded-lg border-[1.2px] border-dashed border-[#d9d6cd] bg-transparent p-1 text-[11px] font-semibold text-[#b0ac9f] transition-all duration-[120ms] hover:border-primary hover:bg-primary/[0.05] hover:text-primary"
                      onClick={() => onAddPersona(segment.segment_id, stage.value)}
                    >
                      <Plus className="h-3 w-3" />
                      <span>ペルソナを追加</span>
                    </button>
                  </div>
                );
              })}
              {/* ⑩: セグメント追加「＋」はヘッダ行のみに残す。この列は
                  .matrix の grid-template-columns で実列（52px）として定義されているため、
                  要素ごと削除すると後続の行がauto-flowでずれる。見えない
                  プレースホルダーで列数を維持したまま「＋」タイルだけ消す。 */}
              <div key={`${stage.value}-add-placeholder`} aria-hidden="true" />
            </React.Fragment>
            );
          })}
        </div>
      </div>
    </div>
  );
};

export default StrategyMatrix;
